import { computed, ref, watch } from 'vue';
import integrationAPI from 'dashboard/api/integrations';
import {
  mockBackInStockAlerts,
  mockCustomers,
  notificationChannels,
} from './fixtures';

const ALL_LOCATIONS_ID = 'all';
const DEFAULT_CURRENCY = 'EUR';
const PRODUCTS_PAGE_SIZE = 50;
const PRODUCT_IMAGE_WIDTH = 200;

/**
 * Converts numeric API values that may arrive as strings into numbers suitable
 * for display and comparisons.
 *
 * @param {number|string|null|undefined} value API numeric value.
 * @returns {number} Parsed number, falling back to zero.
 */
const numberOrZero = value => {
  const parsed = Number(value);
  return Number.isFinite(parsed) ? parsed : 0;
};

/**
 * Returns the quantity to show for a Shopify variant in the product workspace.
 * Location-aware inventory levels take precedence; otherwise the variant-level
 * Shopify inventory quantity is used.
 *
 * @param {Object} variant Cached Shopify variant API payload.
 * @returns {number} Quantity available for seller display.
 */
const variantStock = variant => {
  if (
    variant.available_quantity !== null &&
    variant.available_quantity !== undefined
  ) {
    return numberOrZero(variant.available_quantity);
  }

  return numberOrZero(variant.inventory_quantity);
};

/**
 * Adds a Shopify CDN width hint to product media URLs without changing empty
 * image values.
 *
 * @param {string|null|undefined} imageUrl Product or variant media URL.
 * @returns {string} Image URL constrained for product workspace display.
 */
const productImageUrl = imageUrl => {
  if (!imageUrl) return '';

  try {
    const url = new URL(imageUrl);
    url.searchParams.set('width', String(PRODUCT_IMAGE_WIDTH));
    return url.toString();
  } catch {
    const separator = imageUrl.includes('?') ? '&' : '?';
    return `${imageUrl}${separator}width=${PRODUCT_IMAGE_WIDTH}`;
  }
};

/**
 * Builds the image URL used in rows and drawers from product or variant media.
 *
 * @param {Object} product Cached Shopify product API payload.
 * @returns {string} Best available image URL.
 */
const productImage = product =>
  productImageUrl(
    product.featured_image_url ||
      product.variants?.find(variant => variant.image_url)?.image_url
  );

/**
 * Normalizes one cached Shopify variant into the existing product workspace
 * display contract.
 *
 * @param {Object} variant Cached Shopify variant API payload.
 * @param {Object} product Cached Shopify product API payload.
 * @returns {Object} Product workspace variant.
 */
const normalizeVariant = (variant, product) => ({
  id: String(variant.id),
  externalVariantId: variant.external_variant_id,
  externalInventoryItemId: variant.external_inventory_item_id,
  title: variant.title || product.title,
  sku: variant.sku || '',
  barcode: variant.barcode || '',
  reference: '',
  shopifyVariantId: variant.external_variant_id || '',
  price: numberOrZero(variant.price),
  compareAtPrice: variant.compare_at_price,
  currency: variant.currency || DEFAULT_CURRENCY,
  stock: variantStock(variant),
  selectedOptions: variant.selected_options || [],
  image: productImageUrl(variant.image_url) || productImage(product),
  imageAlt: variant.image_alt || product.featured_image_alt || product.title,
});

/**
 * Normalizes one cached Shopify product into the existing product workspace
 * display contract.
 *
 * @param {Object} product Cached Shopify product API payload.
 * @returns {Object} Product workspace product.
 */
const normalizeProduct = product => {
  const variants = (product.variants || []).map(variant =>
    normalizeVariant(variant, product)
  );
  const firstVariant = variants[0] || {};

  return {
    id: String(product.id),
    externalProductId: product.external_product_id,
    title: product.title,
    sku:
      firstVariant.sku || product.handle || product.external_product_id || '',
    vendor: product.vendor || '',
    handle: product.handle || '',
    sourceUrl: product.online_store_url || '',
    shopifyAdminUrl: product.shopify_admin_url || '',
    productType: product.product_type || '',
    shopifyStatus: product.status || '',
    tags: product.tags || [],
    price: firstVariant.price || 0,
    currency: firstVariant.currency || DEFAULT_CURRENCY,
    image: productImage(product),
    imageAlt: product.featured_image_alt || product.title,
    rawUpdatedAt: product.raw_updated_at,
    variants,
  };
};

/**
 * Normalizes Shopify locations from product-search metadata.
 *
 * @param {Array<Object>} locations Cached Shopify location payloads.
 * @returns {Array<Object>} Store selector options.
 */
const normalizeLocations = locations => [
  { id: ALL_LOCATIONS_ID, name: '', isAll: true },
  ...locations.map(location => ({
    id: String(location.external_location_id || location.id),
    localId: location.id,
    externalLocationId: location.external_location_id,
    name: location.name,
  })),
];

/**
 * Owns API-backed Shopify product state for the product workspace while keeping
 * local back-in-stock alert draft behavior isolated to the UI.
 *
 * @returns {Object} Reactive state and actions for the product workspace.
 */
export function useShopifyProductsState() {
  const searchQuery = ref('');
  const selectedStoreId = ref(ALL_LOCATIONS_ID);
  const products = ref([]);
  const storeLocations = ref([{ id: ALL_LOCATIONS_ID, name: '', isAll: true }]);
  const alerts = ref([...mockBackInStockAlerts]);
  const isLoadingProducts = ref(false);
  const isLoadingMoreProducts = ref(false);
  const productLoadError = ref(false);
  const productPaginationError = ref(false);
  const productMeta = ref({});
  const requestId = ref(0);

  const customers = computed(() => mockCustomers);
  const channels = computed(() => notificationChannels);
  const filteredProducts = computed(() => products.value);
  const hasMoreProducts = computed(() => productMeta.value?.has_more === true);
  const reachedEndOfProducts = computed(
    () => products.value.length > 0 && !hasMoreProducts.value
  );
  const selectedStore = computed(() =>
    storeLocations.value.find(location => location.id === selectedStoreId.value)
  );

  /**
   * Fetches one cached Shopify product page using the current filters.
   *
   * @param {number} page Page number to request.
   * @returns {Promise<Object>} Axios response data.
   */
  function fetchProductsPage(page) {
    const selectedLocation = selectedStore.value;

    return integrationAPI.searchShopifyProducts({
      query: searchQuery.value,
      limit: PRODUCTS_PAGE_SIZE,
      page,
      externalLocationId:
        selectedLocation && !selectedLocation.isAll
          ? selectedLocation.externalLocationId || selectedLocation.id
          : undefined,
      inStockOnly: selectedLocation && !selectedLocation.isAll,
    });
  }

  /**
   * Fetches cached Shopify products using the current search and store filter.
   *
   * @returns {Promise<void>}
   */
  async function fetchProducts() {
    requestId.value += 1;
    const currentRequestId = requestId.value;
    isLoadingProducts.value = true;
    productLoadError.value = false;
    productPaginationError.value = false;

    try {
      const { data } = await fetchProductsPage(1);

      if (currentRequestId !== requestId.value) return;

      products.value = (data.items || []).map(normalizeProduct);
      productMeta.value = data.meta || {};
      if (data.meta?.locations) {
        storeLocations.value = normalizeLocations(data.meta.locations);
      }
    } catch {
      if (currentRequestId !== requestId.value) return;

      productLoadError.value = true;
      products.value = [];
    } finally {
      if (currentRequestId === requestId.value) {
        isLoadingProducts.value = false;
      }
    }
  }

  /**
   * Appends the next Shopify product page when the current result set has more.
   *
   * @returns {Promise<void>}
   */
  async function loadMoreProducts() {
    if (
      isLoadingProducts.value ||
      isLoadingMoreProducts.value ||
      !hasMoreProducts.value
    ) {
      return;
    }

    isLoadingMoreProducts.value = true;
    productPaginationError.value = false;
    const currentRequestId = requestId.value;
    const nextPage = (productMeta.value.current_page || 1) + 1;

    try {
      const { data } = await fetchProductsPage(nextPage);
      if (currentRequestId !== requestId.value) return;

      const existingProductIds = new Set(
        products.value.map(product => product.id)
      );
      const nextProducts = (data.items || [])
        .map(normalizeProduct)
        .filter(product => !existingProductIds.has(product.id));

      products.value = [...products.value, ...nextProducts];
      productMeta.value = data.meta || {};
    } catch {
      if (currentRequestId !== requestId.value) return;

      productPaginationError.value = true;
    } finally {
      if (currentRequestId === requestId.value) {
        isLoadingMoreProducts.value = false;
      }
    }
  }

  /**
   * Stores a local back-in-stock alert for the current browser session.
   *
   * @param {Object} alertDraft Product, variant, customer, and channel draft.
   * @returns {void}
   */
  function createAlert(alertDraft) {
    alerts.value = [
      {
        id: `alert_${Date.now()}`,
        productId: alertDraft.productId,
        variantId: alertDraft.variantId,
        customerId: alertDraft.customerId,
        customer: alertDraft.customer,
        preferredChannels: alertDraft.preferredChannels || [],
        createdAt: new Date().toISOString(),
      },
      ...alerts.value,
    ];
  }

  const waitlistByProduct = computed(() =>
    products.value
      .map(product => {
        const productAlerts = alerts.value.filter(
          alert => alert.productId === product.id
        );
        if (!productAlerts.length) return null;

        const variants = product.variants
          .map(variant => {
            const variantAlerts = productAlerts.filter(
              alert => alert.variantId === variant.id
            );
            if (!variantAlerts.length) return null;

            return {
              ...variant,
              alerts: variantAlerts.map(alert => ({
                ...alert,
                customer:
                  alert.customer ||
                  customers.value.find(
                    customer => customer.id === alert.customerId
                  ),
              })),
            };
          })
          .filter(Boolean);

        return {
          ...product,
          alertCount: productAlerts.length,
          variants,
        };
      })
      .filter(Boolean)
  );

  watch([searchQuery, selectedStoreId], fetchProducts, { immediate: true });

  return {
    searchQuery,
    selectedStoreId,
    channels,
    storeLocations,
    filteredProducts,
    hasMoreProducts,
    reachedEndOfProducts,
    waitlistByProduct,
    createAlert,
    fetchProducts,
    loadMoreProducts,
    isLoadingProducts,
    isLoadingMoreProducts,
    productLoadError,
    productPaginationError,
    productMeta,
  };
}
